import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEvents } from "../context/EventsContext";
import { getSearchPolicy, SEARCH_MODE, MAX_SEARCH_CHARS } from "./SearchPolicy";

export const SEARCH_VIEW = {
    IDLE: "idle",
    TOO_SHORT: "tooShort",
    RESULTS: "results",
    EMPTY: "empty",
};

// State lives here (not in EventsContext) so a keystroke only re-renders the
// keyboard/search bar, never the virtualized event grid.
export default function useSearchController() {
    const { completeLiveEvents, channels, searchEvents } = useEvents();
    const policy = useMemo(() => getSearchPolicy(), []);

    const [query, setRawQuery] = useState("");
    const [results, setResults] = useState([]);
    const [view, setView] = useState(SEARCH_VIEW.IDLE);
    const debounceRef = useRef(null);
    const mountedRef = useRef(true);
    // Mirror the live query so submit() reads it without depending on `query`,
    // keeping its identity stable so OnScreenKeyboard's memo survives keystrokes.
    const queryRef = useRef(query);
    queryRef.current = query;

    const setQuery = useCallback((next) => {
        setRawQuery((prev) => {
            const value = typeof next === "function" ? next(prev) : next;
            return value.slice(0, MAX_SEARCH_CHARS);
        });
    }, []);

    useEffect(() => () => { mountedRef.current = false; }, []);

    const corpus = useMemo(
        () => [...(completeLiveEvents || []), ...(channels || [])],
        [completeLiveEvents, channels]
    );

    const runSearch = useCallback(
        (term) => {
            if (!mountedRef.current) return;
            const trimmed = term.trim();
            if (trimmed.length < policy.minChars) {
                setResults([]);
                setView(trimmed.length > 0 ? SEARCH_VIEW.TOO_SHORT : SEARCH_VIEW.IDLE);
                return;
            }
            const hits = searchEvents(corpus, trimmed);
            setResults(hits);
            setView(hits.length > 0 ? SEARCH_VIEW.RESULTS : SEARCH_VIEW.EMPTY);
        },
        [corpus, searchEvents, policy.minChars]
    );

    // Capable devices search incrementally (debounced); low-end wait for submit().
    useEffect(() => {
        if (policy.mode !== SEARCH_MODE.DEBOUNCE) return undefined;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(query), policy.debounceMs);
        return () => clearTimeout(debounceRef.current);
    }, [query, policy.mode, policy.debounceMs, runSearch]);

    useEffect(() => {
        if (policy.mode === SEARCH_MODE.ENTER) setView(SEARCH_VIEW.IDLE);
    }, [query, policy.mode]);

    const submit = useCallback(() => runSearch(queryRef.current), [runSearch]);

    return { query, setQuery, results, submit, policy, view };
}
